/** Lines that start a known legal table (handled by parseTable.ts). */
export function isTableHeaderLine(line: string): boolean {
  const t = line.trim();
  return /^(Category|Purpose|Right|Term|Name \/ tool|Recipient category|Action|Description)$/i.test(
    t,
  );
}

/** Standalone document headings (no trailing sentence punctuation). */
const DOCUMENT_SUBHEADINGS = new Set([
  "Why We Have Guidelines",
  "Five Core Principles",
  "What Is Not Allowed",
  "Illegal Content and Conduct",
  "Hate, Harassment, and Threats",
  "Sexual and Adult Content",
  "Violent and Graphic Content",
  "Dangerous Activities and Misinformation",
  "Spam, Manipulation, and Inauthentic Behaviour",
  "Privacy and Personal Data",
  "Trip Tracks, Locations, and Safety",
  "Marketplace, Service Quality, and Safety",
  "AI Use",
  "How to Report a Problem",
  "How We Enforce These Guidelines",
  "Appeals",
  "Your Responsibilities Regarding Others' Content",
  "Final Notes",
]);

export function isDocumentSubheading(line: string): boolean {
  return DOCUMENT_SUBHEADINGS.has(line.trim());
}

function isListItemLine(line: string): boolean {
  const t = line.trim();
  return t.length > 0 && t.length < 320 && /[.!?]$/.test(t);
}

export type ProseSegment =
  | { type: "paragraph"; text: string }
  | { type: "list"; intro: string; items: string[] }
  | { type: "subheading"; text: string };

function collapseListParagraphs(segments: ProseSegment[]): ProseSegment[] {
  const out: ProseSegment[] = [];
  let buf: string[] = [];

  const flush = () => {
    if (buf.length >= 2) {
      out.push({ type: "list", intro: "", items: [...buf] });
    } else if (buf.length === 1) {
      out.push({ type: "paragraph", text: buf[0]! });
    }
    buf = [];
  };

  for (const seg of segments) {
    if (seg.type === "paragraph" && isListItemLine(seg.text)) {
      buf.push(seg.text);
    } else {
      flush();
      out.push(seg);
    }
  }
  flush();
  return out;
}

/**
 * Split prose (non-table) text into paragraphs, subheadings, and lists as in the source .txt.
 */
export function splitProseSegments(text: string): ProseSegment[] {
  const lines = text.split(/\r?\n/);
  const segments: ProseSegment[] = [];
  let i = 0;

  while (i < lines.length) {
    const raw = lines[i] ?? "";
    if (!raw.trim()) {
      i += 1;
      continue;
    }

    const line = raw.trim();

    if (isTableHeaderLine(line)) {
      i += 1;
      continue;
    }

    if (isDocumentSubheading(line)) {
      segments.push({ type: "subheading", text: line });
      i += 1;
      continue;
    }

    if (/:\s*$/.test(line)) {
      const intro = line;
      const items: string[] = [];
      i += 1;
      while (i < lines.length) {
        const nextRaw = lines[i] ?? "";
        if (!nextRaw.trim()) break;
        const next = nextRaw.trim();
        if (isTableHeaderLine(next) || isDocumentSubheading(next)) break;
        items.push(next);
        i += 1;
      }
      if (items.length > 0) {
        segments.push({ type: "list", intro, items });
        continue;
      }
      segments.push({ type: "paragraph", text: intro });
      i += 1;
      continue;
    }

    segments.push({ type: "paragraph", text: line });
    i += 1;
  }

  return collapseListParagraphs(segments);
}
